import React, { useState, useMemo } from 'react';
import { matchRecipes } from '../utils/pantryMatcher';

const DIETARY_OPTIONS = ['vegan', 'gluten-free', 'nut-free', 'dairy-free'];

function ScoreBar({ score }) {
  const color =
    score >= 80 ? 'bg-green-500' :
    score >= 50 ? 'bg-yellow-400' :
                  'bg-orange-400';
  return (
    <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2 mt-1">
      <div
        className={`${color} h-2 rounded-full transition-all duration-500`}
        style={{ width: `${score}%` }}
      />
    </div>
  );
}

function MatchCard({ result, onViewRecipe }) {
  const { recipe, matchScore, matched, missing, substituted, canMake } = result;

  return (
    <div className={`bg-pp-white dark:bg-zinc-800 rounded-2xl shadow border p-4 space-y-2
      ${canMake ? 'border-green-500' : 'border-sage-900 dark:border-zinc-600'}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-sage-900 dark:text-zinc-100 leading-snug">{recipe.recipeName}</h3>
        {canMake && (
          <span className="shrink-0 text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">
            Ready to Cook
          </span>
        )}
      </div>

      {/* Score */}
      <div className="text-xs font-semibold text-zinc-500">
        {matchScore}% match
      </div>
      <ScoreBar score={matchScore} />

      {/* Ingredient breakdown */}
      {matched.length > 0 && (
        <p className="text-xs text-green-700">
          <span className="font-semibold">Have:</span> {matched.join(', ')}
        </p>
      )}
      {substituted.length > 0 && (
        <p className="text-xs text-yellow-700">
          <span className="font-semibold">Can sub:</span> {substituted.join(', ')}
        </p>
      )}
      {missing.length > 0 && (
        <p className="text-xs text-red-600">
          <span className="font-semibold">Need:</span> {missing.join(', ')}
        </p>
      )}

      <button
        onClick={() => onViewRecipe(recipe)}
        className="mt-1 text-xs font-bold text-sage-900 dark:text-zinc-300 underline underline-offset-2"
      >
        View Recipe →
      </button>
    </div>
  );
}

/**
 * PantryMatcher
 *
 * Props:
 *   recipes       — full recipe array (from ChefBuddeeUI)
 *   substitutions — from Supabase (optional, defaults to [])
 *   onViewRecipe  — callback(recipe) to open the recipe in the main view
 */
export default function PantryMatcher({ recipes = [], substitutions = [], onViewRecipe, userType = 'free', totalRecipeCount = 0 }) {
  const [inputValue, setInputValue]     = useState('');
  const [pantryItems, setPantryItems]   = useState([]);
  const [dietaryFilters, setDietary]    = useState([]);
  const [minScore, setMinScore]         = useState(30);
  const [hasSearched, setHasSearched]   = useState(false);

  const addItem = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    const items = trimmed.split(',').map(s => s.trim()).filter(Boolean);
    setPantryItems(prev => {
      const existing = new Set(prev.map(i => i.toLowerCase()));
      const fresh = items.filter(i => !existing.has(i.toLowerCase()));
      return [...prev, ...fresh];
    });
    setInputValue('');
  };

  const removeItem = (item) =>
    setPantryItems(prev => prev.filter(i => i !== item));

  const toggleDietary = (filter) =>
    setDietary(prev =>
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    );

  const results = useMemo(() => {
    if (!hasSearched || !pantryItems.length) return [];
    return matchRecipes({ recipes, pantryItems, substitutions, dietaryFilters, minScore });
  }, [hasSearched, pantryItems, substitutions, dietaryFilters, minScore, recipes]);

  return (
    <div className="bg-sage-100 dark:bg-zinc-900 border border-sage-900 dark:border-zinc-700 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-extrabold text-sage-900 dark:text-zinc-100">Pantry Match</h2>
        <span className="text-xs bg-periwinkle-100 text-periwinkle-900 font-semibold px-2 py-0.5 rounded-full">
          What can I make?
        </span>
      </div>
      {userType === 'free' && totalRecipeCount > 10 && (
        <p className="text-xs text-honeyYellow-900 font-semibold bg-honeyYellow-100 border border-honeyYellow-900/30 rounded-lg px-3 py-2">
          Matching against 10 of {totalRecipeCount} recipes — upgrade to search the full library.
        </p>
      )}

      {/* Ingredient input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addItem()}
          placeholder="Add ingredients (comma-separated)…"
          className="flex-1 p-3 rounded-xl border border-sage-900 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 text-sm"
        />
        <button
          onClick={addItem}
          className="px-4 py-2 bg-sage-900 text-white font-bold rounded-xl text-sm"
        >
          Add
        </button>
      </div>

      {/* Pantry chips */}
      {pantryItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pantryItems.map(item => (
            <span
              key={item}
              className="flex items-center gap-1 bg-sage-900 text-white text-xs font-semibold px-3 py-1 rounded-full"
            >
              {item}
              <button
                onClick={() => removeItem(item)}
                className="ml-1 text-white/70 hover:text-white font-bold"
              >
                ×
              </button>
            </span>
          ))}
          <button
            onClick={() => { setPantryItems([]); setHasSearched(false); }}
            className="text-xs text-red-500 font-semibold underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Dietary filters */}
      <div className="flex flex-wrap gap-2">
        {DIETARY_OPTIONS.map(opt => (
          <button
            key={opt}
            onClick={() => toggleDietary(opt)}
            className={`text-xs font-semibold px-3 py-1 rounded-full border transition ${
              dietaryFilters.includes(opt)
                ? 'bg-periwinkle-900 text-white border-periwinkle-900'
                : 'bg-white dark:bg-zinc-800 text-sage-900 dark:text-zinc-300 border-sage-900 dark:border-zinc-600'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Min match slider */}
      <div className="flex items-center gap-3 text-sm">
        <span className="text-xs font-semibold text-sage-900 dark:text-zinc-300 shrink-0">
          Min match: {minScore}%
        </span>
        <input
          type="range" min="10" max="90" step="10"
          value={minScore}
          onChange={e => setMinScore(Number(e.target.value))}
          className="flex-1 accent-sage-900"
        />
      </div>

      {/* Search button */}
      <button
        disabled={!pantryItems.length}
        onClick={() => setHasSearched(true)}
        className="w-full bg-sage-900 text-white font-bold py-3 rounded-full disabled:opacity-40 transition"
      >
        Find Recipes
      </button>

      {/* Results */}
      {hasSearched && (
        <div className="space-y-3 pt-2">
          {results.length === 0 ? (
            <p className="text-center text-sm text-zinc-500 italic py-4">
              No recipes matched {minScore}%+ with your pantry.
              Try lowering the minimum match or adding more ingredients.
            </p>
          ) : (
            <>
              <p className="text-xs text-zinc-500 font-semibold">
                {results.length} recipe{results.length !== 1 ? 's' : ''} found
                {results.filter(r => r.canMake).length > 0 &&
                  ` · ${results.filter(r => r.canMake).length} ready to cook now`}
              </p>
              {results.map((result, i) => (
                <MatchCard
                  key={result.recipe.recipeName + i}
                  result={result}
                  onViewRecipe={onViewRecipe || (() => {})}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
